import React from "react";
import PotentialMetrices from "./PotentialMetrices";
import PromotionMetrices from "./PromotionMetrices";
import SuccessorComponent from "./Successor";
import EmployeeManagerDetail from "./EmployeeManagerDetail";
import { useState, useEffect } from "react";
import { employeesURL, successorURL } from "../Config";
import { useAppContext } from "./AppContext";


import { 
  Form, 
  Col, 
  Row, 
  Typography, 
  Button, 
  Space, 
  message, 
  Select, 
  Card, 
  Divider, 
  Progress, 
  Badge,
  Steps,
  Tooltip,
  Alert
} from "antd";
import { 
  SaveOutlined, 
  UserOutlined, 
  TrophyOutlined,
  RocketOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CloseOutlined,
  StarOutlined
} from "@ant-design/icons";

const { Text, Title } = Typography;

// 9-Box Mapping Component
const NineBoxMapping = ({ performanceRating, potentialRating }) => {
  const getNineBoxPosition = (performance, potential) => {
    // Early return if either rating is missing
    if (!performance || performance === '' || !potential || potential === '') {
      return { position: 'Not Assigned', color: '#999', backgroundColor: '#f5f5f5', icon: '❔', type: 'Assessment\nPending' };
    }
    
    // Convert numeric ratings to text descriptions
    const getPotentialText = (rating) => {
      if (!rating) return "";
      switch (rating.toString()) {
        case "1": return "Inconsistent";
        case "2": return "Maxed Out";
        case "3": return "Successful";
        case "4": return "Ready For More";
        case "5": return "Eager To Expand";
        default: return "";
      }
    };

    const getPerformanceText = (rating) => {
      if (!rating) return "";
      switch (rating.toString()) {
        case "1": return "Does Not Meet Expectations";
        case "2": return "Partially Meets Expectations";
        case "3": return "Consistently Meets Expectations";
        case "4": return "Meets And Often Exceeds Expectations";
        case "5": return "Always Exceeds Expectations";
        default: return "";
      }
    };

    // Handle both string and numeric inputs for potential rating
    let potentialText;
    if (typeof potential === 'string' && potential.length > 1) {
      // If it's already a string description, use it
      potentialText = potential;
    } else {
      // If it's a numeric value, convert it
      potentialText = getPotentialText(potential);
    }
    
    // Handle both string and numeric inputs for performance rating
    let performanceText;
    if (typeof performance === 'string' && performance.length > 1 && isNaN(performance)) {
      // If it's already a string description, use it
      performanceText = performance;
    } else {
      // If it's a numeric value, convert it
      performanceText = getPerformanceText(performance);
    }

    // Apply the business logic for 9-box classification
    const getClassification = (potentialRating, performanceRating) => {
      const potLower = potentialRating.toLowerCase();
      const perfLower = performanceRating.toLowerCase();

      if ((potLower === "inconsistent" || potLower === "maxed out") && 
          (perfLower === "does not meet expectations" || perfLower === "partially meets expectations")) {
        return "Poor Fit";
      }
      else if ((potLower === "inconsistent" || potLower === "maxed out") && 
               (perfLower === "consistently meets expectations")) {
        return "Suitably Placed";
      }
      else if ((potLower === "inconsistent" || potLower === "maxed out") && 
               (perfLower === "meets and often exceeds expectations" || perfLower === "always exceeds expectations")) {
        return "Valued Professional";
      }
      else if ((potLower === "successful") && 
               (perfLower === "does not meet expectations" || perfLower === "partially meets expectations")) {
        return "Needs Performance Management";
      }
      else if ((potLower === "successful") && 
               (perfLower === "consistently meets expectations")) {
        return "Solid Performer";
      }
      else if ((potLower === "successful") && 
               (perfLower === "meets and often exceeds expectations" || perfLower === "always exceeds expectations")) {
        return "Promotable";
      }
      else if ((potLower === "ready for more" || potLower === "eager to expand") && 
               (perfLower === "does not meet expectations" || perfLower === "partially meets expectations")) {
        return "Needs Improvement";
      }
      else if ((potLower === "ready for more" || potLower === "eager to expand") && 
               (perfLower === "consistently meets expectations")) {
        return "Expandable";
      }
      else {
        return "Top Talent";
      }
    };

    if (!potentialText || !performanceText) {
      // If we have performance rating but no potential rating, show a partial classification
      if (performanceText && !potentialText) {
        return { 
          position: 'Pending Potential Assessment', 
          color: '#1890ff', 
          backgroundColor: '#e6f7ff', 
          icon: '⏳',
          type: 'Potential Assessment\nRequired'
        };
      }
      return { position: 'Not Assigned', color: '#999', backgroundColor: '#f5f5f5', icon: '❔', type: 'Assessment\nPending' };
    }

    const classification = getClassification(potentialText, performanceText);

    // Color mapping based on classification
    const colorMapping = {
      'Poor Fit': { color: '#C51E1E', backgroundColor: '#F9ECEC', icon: '❌', type: 'Low Performance\nLow Potential' },
      'Needs Performance Management': { color: '#C51E1E', backgroundColor: '#F9ECEC', icon: '📋', type: 'Low Performance\nMedium Potential' },
      'Needs Improvement': { color: '#C51E1E', backgroundColor: '#F9ECEC', icon: '⚠️', type: 'Low Performance\nHigh Potential' },
      'Suitably Placed': { color: '#D68910', backgroundColor: '#FCF3CF', icon: '🔧', type: 'Medium Performance\nLow Potential' },
      'Solid Performer': { color: '#D68910', backgroundColor: '#FCF3CF', icon: '📈', type: 'Medium Performance\nMedium Potential' },
      'Valued Professional': { color: '#D68910', backgroundColor: '#FCF3CF', icon: '💼', type: 'High Performance\nLow Potential' },
      'Promotable': { color: '#008000', backgroundColor: '#DCF3D9', icon: '🌟', type: 'High Performance\nMedium Potential' },
      'Expandable': { color: '#008000', backgroundColor: '#DCF3D9', icon: '🚀', type: 'Medium Performance\nHigh Potential' },
      'Top Talent': { color: '#008000', backgroundColor: '#DCF3D9', icon: '⭐', type: 'High Performance\nHigh Potential' }
    };
    return {
      position: classification,
      ...colorMapping[classification] || { color: '#999', backgroundColor: '#f5f5f5', icon: '❔', type: 'Not Classified' }
    };
  };

  const renderClassificationText = (text) => {
    // Handle empty or undefined 9Box mapping
    if (!text || text.trim() === '') {
      return <span style={{ color: '#999', fontStyle: 'italic' }}>Not Assigned</span>;
    }

    let color;
    let backgroundColor;
    
    // Normalize the text for comparison (trim and handle case)
    const normalizedText = text.trim();
    
    if (normalizedText === 'Poor Fit' || normalizedText === 'Needs Performance Management' || normalizedText === 'Needs Improvement') {
      color = '#C51E1E';
      backgroundColor = '#F9ECEC';
    } else if (normalizedText === 'Promotable' || normalizedText === 'Expandable' || normalizedText === 'Top Talent') {
      color = '#008000';
      backgroundColor = '#DCF3D9';
    } else if (normalizedText === 'Suitably Placed' || normalizedText === 'Solid Performer' || normalizedText === 'Valued Professional') {
      color = '#D68910';
      backgroundColor = '#FCF3CF';
    } else {
      color = '#333';
      backgroundColor = '#f5f5f5';
    }

    return (
      <span style={{ 
        color: color,
        backgroundColor: backgroundColor,
        padding: '4px 8px',
        borderRadius: '4px',
        fontWeight: '500'
      }}>
        {text}
      </span>
    );
  };

  const boxData = getNineBoxPosition(performanceRating, potentialRating);

  return (
    <Card 
      size="small"
      style={{ 
        border: `2px solid ${boxData.color}`,
        borderRadius: '12px',
        backgroundColor: boxData.backgroundColor,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        minHeight: '60px'
      }}
      bodyStyle={{ padding: '0px', textAlign: 'center' }}
    >
      <Title 
        level={5} 
        style={{ 
          color: boxData.color,
          margin: '0 0 5px 0',
          fontWeight: '600'
        }}
      >
        {boxData.position}
      </Title>
      {boxData.type && (
        <div style={{ 
          fontSize: '11px', 
          color: boxData.color,
          opacity: 0.8,
          fontWeight: '500',
          lineHeight: '1.2',
          whiteSpace: 'pre-line',
          marginBottom: '8px'
        }}>
          {boxData.type}
        </div>
      )}
    </Card>
  );
};

// Assessment Progress Component
const AssessmentProgress = ({ assessmentValues }) => {
  const { 
    abilityValue, 
    aspirationValue, 
    adaptabilityValue, 
    readinessValue, 
    isReadyValue, 
    performancerating 
  } = assessmentValues;

  const calculateProgress = () => {
    let completed = 0;
    const total = 6; // Total assessment fields

    if (performancerating && performancerating !== '') completed++;
    if (abilityValue && abilityValue !== '') completed++;
    if (aspirationValue && aspirationValue !== '') completed++;
    if (adaptabilityValue && adaptabilityValue !== '') completed++;
    if (readinessValue && readinessValue !== '') completed++;
    if (isReadyValue && isReadyValue !== '') completed++;

    return Math.round((completed / total) * 100);
  };

  const progress = calculateProgress();
  const isComplete = progress === 100;

  return (
    <Card 
      size="small" 
      style={{ 
        marginBottom: '16px',
        borderRadius: '8px',
        border: `1px solid ${isComplete ? '#52c41a' : '#d9d9d9'}`
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ flex: 1 }}>
          <Text strong style={{ color: 'white' }}>Assessment Progress</Text>
          <Progress 
            percent={progress}
            size="small"
            strokeColor={isComplete ? '#52c41a' : '#1890ff'}
            status={isComplete ? 'success' : 'active'}
            trailColor="rgba(255,255,255,0.3)"
          />
        </div>
        <Badge 
          count={isComplete ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : `${progress}%`}
          style={{ backgroundColor: isComplete ? '#52c41a' : '#1890ff' }}
        />
      </div>
    </Card>
  );
};

const AssessmentPage = ({ data, handleRefresh, onSaveComplete }) => {
  const [isInitialRender, setIsInitialRender] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [textValue, setTextValue] = useState(data?.comments || '');
  const [abilityValue, setAbility] = useState(data?.ability || '');
  const [aspirationValue, setAspiration] = useState(data?.aspiration || '');
  const [adaptabilityValue, setAdaptability] = useState(data?.adaptability || '');
  const [readinessValue, setReadiness] = useState(data?.readiness || '');
  const [isReadyValue, setIsReady] = useState(data?.isPromoReady || '');
  const [performancerating, setPerformancerating] = useState(data?.performancerating || '');
  const { idToken } = useAppContext();

  const { Option } = Select;

  const [successorData, setsuccessorData] = useState([
    {
      empName: '',
      empMailId: '',
      successorMailID: '',
      manager: '',
      title: '',
      readiness: '',
      comments: ''
    }
  ]);

 
  console.log('Data:', data);

  message.config({
    className: 'ant-message-custom-content',
    duration: 3, // Adjust the duration as needed
  });

  // Assessment values for progress tracking
  const assessmentValues = {
    abilityValue,
    aspirationValue,
    adaptabilityValue,
    readinessValue,
    isReadyValue,
    performancerating
  };

  useEffect(() => {
    
    // Only proceed if data is available
    if (!data) {
      // Reset all fields when no data
      setAbility('');
      setAspiration('');
      setAdaptability('');
      setReadiness('');
      setTextValue('');
      setIsReady('');
      setPerformancerating('');
      return;
    }

    console.log('Use Effect Triggered');
    console.log('Initial data received:', data);
    console.log('Individual field values:', {
      ability: data.ability,
      aspiration: data.aspiration,
      adaptability: data.adaptability,
      readiness: data.readiness,
      isPromoReady: data.isPromoReady,
      performancerating: data.performancerating,
      comments: data.comments
    });
    
    setIsInitialRender(true);
    //console.log('isInitialRender:', isInitialRender);
    setAbility(data?.ability || '');
    setAspiration(data?.aspiration || '');
    setAdaptability(data?.adaptability || '');
    setReadiness(data?.readiness || '');
    setTextValue(data?.comments || '');
    setIsReady(data?.isPromoReady || '');
    setPerformancerating(data?.performancerating || '');
    setsuccessorData([])

    // Define the function to fetch data
    const fetchSuccessorData = async () => {
      try {
        const url = successorURL +'/GetSuccessors';
    const requestData = {
      userId: data.userPrincipalName
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify(requestData)
    });

    if (response.ok) {
      const data = await response.json();
      setsuccessorData(data); // Assuming the response is an array of successors
    } else {
      //console.error('Error fetching data:', response.status, response.statusText);
    }
      } catch (error) {
        //console.error('Error fetching data:', error);
      }
    };

    // Call the function to fetch data
    fetchSuccessorData();
    setIsInitialRender(false);
    
    // Debug: Log the state values after setting them
    console.log('After setting state values:', {
      abilityValue: data.ability,
      aspirationValue: data.aspiration,
      adaptabilityValue: data.adaptability,
      readinessValue: data.readiness,
      isReadyValue: data.isPromoReady,
      performancerating: data.performancerating
    });
  }, [data, data?.userPrincipalName, data?.userEmail]);

  // Add useEffect to log state changes for debugging
  useEffect(() => {
    console.log('State values updated:', {
      abilityValue,
      aspirationValue,
      adaptabilityValue,
      readinessValue,
      isReadyValue,
      performancerating
    });
  }, [abilityValue, aspirationValue, adaptabilityValue, readinessValue, isReadyValue, performancerating]);

  // Add useEffect specifically for performance rating debugging
  useEffect(() => {
    if (data?.performancerating !== undefined) {
      console.log('Performance rating from data prop:', data.performancerating);
      console.log('Current performance rating state:', performancerating);
      if (data.performancerating !== performancerating) {
        console.log('Performance rating mismatch detected, updating state');
        setPerformancerating(data.performancerating || '');
      }
    }
  }, [data?.performancerating, data?.userPrincipalName]);

  const saveEmployeeData = async (potentialRating) => {
    const apiData = {
      userEmail: data.userEmail,
      userPrincipalName: data.userPrincipalName,
      potentialRating: potentialRating.toString(),
      performanceRating: performancerating,
      ability: abilityValue,
      aspiration: aspirationValue,
      adaptability: adaptabilityValue,
      readiness: readinessValue,
      comment: textValue,
      isPromoReady: isReadyValue,
      commentDate: new Date().toISOString()
    };
  
    //console.log('API Data:', apiData);
  
    try {
      const response = await fetch(employeesURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
           'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(apiData)
      });
  
      if (!response.ok) {
        throw new Error('Failed to save data');
      }
  
      const data = await response.json();
      //console.log('Data saved successfully:', data);

      message.success("Assessment saved successfully!");

      //console.log('Calling handleRefresh');
      handleRefresh(); // Ensure refresh is called after save is successful
    } catch (error) {
      //console.error('Error saving data:', error);
      message.error('Failed to save assessment');
      throw error;
    }
  };
  

  const handlesave = async () => {
    setIsSaving(true);
    
    try {
      console.log('Handle Save called');
      console.log('Current values:', {
        abilityValue,
        aspirationValue,
        adaptabilityValue,
        readinessValue,
        isReadyValue,
        performancerating
      });
      
      // Check for missing required fields - check for undefined, null, or empty string (but allow "0" as valid)
      const missingFields = [];
      if (!abilityValue || abilityValue === '' || abilityValue === null || abilityValue === undefined) missingFields.push('Ability');
      if (!aspirationValue || aspirationValue === '' || aspirationValue === null || aspirationValue === undefined) missingFields.push('Aspiration');
      if (!adaptabilityValue || adaptabilityValue === '' || adaptabilityValue === null || adaptabilityValue === undefined) missingFields.push('Engagement');
      if (!readinessValue || readinessValue === '' || readinessValue === null || readinessValue === undefined) missingFields.push('Readiness');
      if (!isReadyValue || isReadyValue === '' || isReadyValue === null || isReadyValue === undefined) missingFields.push('Ready For Promotion');
      
      if (missingFields.length > 0) {
        message.error(`Please complete all required assessments: ${missingFields.join(', ')}`);
        return;
      }
    
      
      const abilityFloatValue = parseFloat(abilityValue);
      const aspirationFloatValue = parseFloat(aspirationValue);
      const adaptabilityFloatValue = parseFloat(adaptabilityValue);
    
      // Calculate the average of the three values
      const averageRating = Math.ceil((abilityFloatValue + aspirationFloatValue + adaptabilityFloatValue) / 3);
      //console.log('Average Rating:', averageRating);
    
      await saveEmployeeData(averageRating);
      //console.log('Successor Data', successorData);
      
      if (successorData.length > 0) {
        try {
          //console.log('Executing API call for successor data:', successorData)
          const response = await fetch(successorURL, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify(successorData)
          });
      
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
      
          const responseData = await response.json();
          //console.log('Success:', responseData);
          //console.log('Calling handleRefresh');
          handleRefresh(); // Refresh after successor data is saved
          
          // Close modal after successful save
          if (onSaveComplete) {
            setTimeout(() => onSaveComplete(), 500); // Small delay to show success message
          }
        } catch (error) {
          //console.error('Error:', error);
          //console.log('Calling handleRefresh on error');
          handleRefresh(); // Ensure refresh is called in case of error as well
        }
      } else {
        //console.log('No rows in successorData. Deleting all successors');
        try {
          const response = await fetch(successorURL + `/${data.userPrincipalName}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            }
          });

          if (response.status === 204) {
            //console.log('Resource deleted successfully.');
          } else if (response.ok) {
            //console.log('Resource deleted successfully.');
          } else {
            //console.log('Failed to delete the resource.');
          }
          
          // Close modal after successful save (no successor case)
          if (onSaveComplete) {
            setTimeout(() => onSaveComplete(), 500); // Small delay to show success message
          }
        } catch (error) {
          //console.log(`Error: ${error.message}`);
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleTextareaChange = (e) => {
    setTextValue(e.target.value);
  };

  const handleReadinessChange = (value) => {
    setReadiness(value);
  }

  const handleIsReadyChange = (value) => {
    setIsReady(value);
  }

  const handleAbilityChange = (value) => {
    //console.log('Change Event', value)
    setAbility(value);
  }

  const handleAspirationChange = (value) => {
    setAspiration(value);
  }

  const handleAdaptabilityChange = (value) => {
    setAdaptability(value);
  }

  const HandlePerformanceratingChange = (value) => {
    setPerformancerating(value);
  }

  // Remove the auto-save useEffect - now only saves when user clicks "Save Assessment" button

  // Calculate potential rating based on ability, aspiration, and adaptability
  const calculatePotentialRating = () => {
    if (!abilityValue || !aspirationValue || !adaptabilityValue) return '';
    const abilityFloatValue = parseFloat(abilityValue);
    const aspirationFloatValue = parseFloat(aspirationValue);
    const adaptabilityFloatValue = parseFloat(adaptabilityValue);
    
    if (isNaN(abilityFloatValue) || isNaN(aspirationFloatValue) || isNaN(adaptabilityFloatValue)) return '';
    
    return Math.ceil((abilityFloatValue + aspirationFloatValue + adaptabilityFloatValue) / 3);
  };

  const potentialRating = calculatePotentialRating();

  // For initial display, also consider data from props if state values are not available
  const displayPerformanceRating = performancerating && performancerating !== '' ? performancerating : (data?.performancerating || '');
  
  // Helper function to convert performance rating to text for 9-Box mapping
  const getDisplayPerformanceText = (rating) => {
    if (!rating) return "";
    switch (rating.toString()) {
      case "1": return "Does Not Meet Expectations";
      case "2": return "Partially Meets Expectations";
      case "3": return "Consistently Meets Expectations";
      case "4": return "Meets And Often Exceeds Expectations";
      case "5": return "Always Exceeds Expectations";
      default: return rating; // Return as-is if it's already text
    }
  };
  
  // Get the text version of performance rating for 9-Box mapping
  const performanceRatingForNineBox = getDisplayPerformanceText(displayPerformanceRating);
  
  // Calculate potential rating from props data if state values are not available
  const getInitialPotentialRating = () => {
    // Helper function to convert numeric rating to string
    const getPotentialText = (rating) => {
      if (!rating) return "";
      switch (rating.toString()) {
        case "1": return "Inconsistent";
        case "2": return "Maxed Out";
        case "3": return "Successful";
        case "4": return "Ready For More";
        case "5": return "Eager To Expand";
        default: return "";
      }
    };

    // First try to get from current state values (only if all three are available and not empty)
    if (abilityValue && abilityValue !== '' && aspirationValue && aspirationValue !== '' && adaptabilityValue && adaptabilityValue !== '') {
      const ability = parseFloat(abilityValue);
      const aspiration = parseFloat(aspirationValue);
      const adaptability = parseFloat(adaptabilityValue);
      
      if (!isNaN(ability) && !isNaN(aspiration) && !isNaN(adaptability)) {
        const numericRating = Math.ceil((ability + aspiration + adaptability) / 3);
        return getPotentialText(numericRating); // Return string value
      }
    }
    
    // Then try to get from props data (fallback for initial render)
    if (data?.ability && data?.ability !== '' && data?.aspiration && data?.aspiration !== '' && data?.adaptability && data?.adaptability !== '') {
      const ability = parseFloat(data.ability);
      const aspiration = parseFloat(data.aspiration);
      const adaptability = parseFloat(data.adaptability);
      
      if (!isNaN(ability) && !isNaN(aspiration) && !isNaN(adaptability)) {
        const numericRating = Math.ceil((ability + aspiration + adaptability) / 3);
        return getPotentialText(numericRating); // Return string value
      }
    }
    
    // If no complete potential data is available, return empty string
    return '';
  };
  
  const displayPotentialRating = getInitialPotentialRating();

  // Also log the numeric potential rating for debugging
  const numericPotentialRating = calculatePotentialRating();
  console.log("Potential Rating (Numeric):",numericPotentialRating);

  return (
    <div style={{ 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      height: '100vh',
      overflow: 'auto',
      padding: '24px'
    }}>
      <style>
        {`          
          .promotion-tooltip .ant-tooltip-inner,
          .performance-tooltip .ant-tooltip-inner {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 8px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
            padding: 8px 12px;
            font-size: 13px;
            line-height: 1.4;
          }
        `}
      </style>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header Section */}
        <Card 
          style={{ 
            marginBottom: '16px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            border: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
          }}
          bodyStyle={{ padding: '16px' }}
        >
          <div style={{ color: 'white', textAlign: 'center' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              marginBottom: '0px' 
            }}>
              <div>
                <Title 
                  level={2} 
                  style={{ 
                    margin: 0, 
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '20px'
                  }}
                >
                  Employee Performance - Potential Mapping
                </Title>
              </div>
            </div>
          </div>
        </Card>

        {/* Employee Information - Compact Section */}
        <Card 
          style={{ 
            marginBottom: '20px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            border: '1px solid #e8f4fd'
          }}
          bodyStyle={{
            padding: '12px 16px'
          }}
        >
          <Row align="middle" gutter={[16, 8]} style={{ flexWrap: 'nowrap' }}>
            {/* Employee Basic Info */}
            <Col flex="0 0 auto">
              <Space align="center" size={12}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: 'linear-gradient(45deg, #1890ff, #36cfc9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold'
                }}>
                  {data?.userName ? data.userName.charAt(0).toUpperCase() : 'E'}
                </div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '16px', color: '#262626', marginBottom: '2px' }}>
                    {data?.userName || 'Employee Name'}
                  </div>
                  <div style={{ color: '#8c8c8c', fontSize: '12px' }}>
                    {data?.jobTitle || 'Job Title'}
                  </div>
                </div>
              </Space>
            </Col>

            {/* Divider */}
            <Col flex="0 0 auto">
              <div style={{ 
                width: '1px', 
                height: '40px', 
                background: 'linear-gradient(to bottom, transparent, #d9d9d9, transparent)',
                margin: '0 8px'
              }} />
            </Col>

            {/* Manager Info */}
            <Col flex="0 0 auto">
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '2px' }}>
                  👤 Manager
                </div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>
                  {data?.managerName || 'Not specified'}
                </div>
              </div>
            </Col>

            {/* Department Info */}
            <Col flex="0 0 auto">
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '2px' }}>
                  🏢 Department
                </div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>
                  {data?.department || 'Not specified'}
                </div>
              </div>
            </Col>

            {/* Location Info */}
            <Col flex="0 0 auto">
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '2px' }}>
                  📍 Location
                </div>
                <div style={{ fontSize: '13px', fontWeight: '500', color: '#595959' }}>
                  {data?.location || 'Not specified'}
                </div>
              </div>
            </Col>

            {/* Email Info - Right aligned */}
            <Col flex="1 1 auto" style={{ textAlign: 'right' }}>
              {data?.userEmail && (
                <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                  📧 {data.userEmail}
                </div>
              )}
            </Col>
          </Row>
        </Card>

        {/* Main Content Grid */}
        <Row gutter={[24, 24]}>
          {/* Left Column */}
          <Col xs={24} lg={16}>

            {/* Performance Assessment */}
            <Card 
              title={
                <Space>
                  <TrophyOutlined style={{ color: '#fa8c16' }} />
                  <Tooltip 
                    title="Default as per recent appraisal rating"
                    placement="top"
                    mouseEnterDelay={0}
                    mouseLeaveDelay={0}
                    overlayClassName="performance-tooltip"
                  >
                    <span 
                      style={{ 
                        fontWeight: 'bold', 
                        color: '#333'
                      }}
                    >
                      Performance Enablement Rating
                    </span>
                  </Tooltip>
                </Space>
              }
              style={{ 
                marginBottom: '24px',
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
              }}
              headStyle={{ 
                background: 'linear-gradient(90deg, #fff7e6 0%, #fef3e2 100%)',
                borderRadius: '12px 12px 0 0'
              }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <div>
                  <Text strong style={{ marginBottom: '8px', display: 'block' }}>Overall Performance Rating</Text>
                  <Select 
                    key={`performance-${data?.userPrincipalName}-${data?.userEmail}`}
                    value={performancerating} 
                    onChange={HandlePerformanceratingChange} 
                    style={{ width: '100%' }} 
                    placeholder="Select performance rating"
                    size="large"
                  >
                    <Option value="5">Always Exceeds Expectations</Option>
                    <Option value="4">Meets and Often Exceeds Expectations</Option>
                    <Option value="3">Consistently Meets Expectations</Option>
                    <Option value="2">Partially Meets Expectations</Option>
                    <Option value="1">Does Not Meet Expectations</Option>
                  </Select>
                </div>
              </Space>
            </Card>

            {/* Potential Rating */}
            <Card 
              title={
                <Space>
                  <StarOutlined style={{ color: '#1890ff' }} />
                  <span>Potential Rating</span>
                </Space>
              }
              style={{ 
                marginBottom: '24px',
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
              }}
              headStyle={{ 
                background: 'linear-gradient(90deg, #e6f7ff 0%, #d6f7ff 100%)',
                borderRadius: '12px 12px 0 0'
              }}
            >
              <PotentialMetrices 
                key={`potential-${data?.userPrincipalName}-${abilityValue}-${aspirationValue}-${adaptabilityValue}`}
                ability={abilityValue} 
                aspiration={aspirationValue} 
                adaptability={adaptabilityValue} 
                onAbilityChange={handleAbilityChange} 
                onAspirationChange={handleAspirationChange} 
                onAdapatabilityChange={handleAdaptabilityChange} 
              />
            </Card>

          </Col>

          {/* Right Column - 9-Box and Summary */}
          <Col xs={24} lg={8}>
            <Space direction="vertical" style={{ width: '100%' }}>
              {/* 9-Box Mapping */}
              <Card 
                title="9-Box Classification"
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
                }}
                headStyle={{ 
                  background: 'linear-gradient(90deg, #fff1f0 0%, #ffe7e6 100%)',
                  borderRadius: '12px 12px 0 0',
                  textAlign: 'center'
                }}
              >
                <NineBoxMapping 
                  key={`ninebox-${data?.userPrincipalName}-${performanceRatingForNineBox}-${displayPotentialRating}`}
                  performanceRating={performanceRatingForNineBox}
                  potentialRating={displayPotentialRating}
                />
              </Card>

              {/* 9-Box Grid Overview */}
              <Card 
                title="9-Box Grid Overview"
                style={{ 
                  borderRadius: '12px',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                  marginTop: '1px'
                }}
                headStyle={{ 
                  background: 'linear-gradient(90deg, #f0f9ff 0%, #e0f2fe 100%)',
                  borderRadius: '12px 12px 0 0',
                  textAlign: 'center'
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '3px', fontSize: '10px' }}>
                  {(() => {
                    // Get the current employee's 9-box position
                    const currentBoxData = (() => {
                      if (!performanceRatingForNineBox || !displayPotentialRating) return null;
                      
                      const potLower = displayPotentialRating.toLowerCase();
                      const perfLower = performanceRatingForNineBox.toLowerCase();

                      if ((potLower === "inconsistent" || potLower === "maxed out") && 
                          (perfLower === "does not meet expectations" || perfLower === "partially meets expectations")) {
                        return "Poor Fit";
                      }
                      else if ((potLower === "inconsistent" || potLower === "maxed out") && 
                               (perfLower === "consistently meets expectations")) {
                        return "Suitably Placed";
                      }
                      else if ((potLower === "inconsistent" || potLower === "maxed out") && 
                               (perfLower === "meets and often exceeds expectations" || perfLower === "always exceeds expectations")) {
                        return "Valued Professional";
                      }
                      else if ((potLower === "successful") && 
                               (perfLower === "does not meet expectations" || perfLower === "partially meets expectations")) {
                        return "Needs Performance Management";
                      }
                      else if ((potLower === "successful") && 
                               (perfLower === "consistently meets expectations")) {
                        return "Solid Performer";
                      }
                      else if ((potLower === "successful") && 
                               (perfLower === "meets and often exceeds expectations" || perfLower === "always exceeds expectations")) {
                        return "Promotable";
                      }
                      else if ((potLower === "ready for more" || potLower === "eager to expand") && 
                               (perfLower === "does not meet expectations" || perfLower === "partially meets expectations")) {
                        return "Needs Improvement";
                      }
                      else if ((potLower === "ready for more" || potLower === "eager to expand") && 
                               (perfLower === "consistently meets expectations")) {
                        return "Expandable";
                      }
                      else {
                        return "Top Talent";
                      }
                    })();

                    const categories = [
                      { name: "Needs Improvement", color: '#C51E1E', bgColor: '#F9ECEC', desc: 'Low Performance\nHigh Potential' },
                      { name: "Expandable", color: '#008000', bgColor: '#DCF3D9', desc: 'Medium Performance\nHigh Potential' },
                      { name: "Top Talent", color: '#008000', bgColor: '#DCF3D9', desc: 'High Performance\nHigh Potential' },
                      { name: "Needs Performance Management", color: '#C51E1E', bgColor: '#F9ECEC', desc: 'Low Performance\nMedium Potential' },
                      { name: "Solid Performer", color: '#D68910', bgColor: '#FCF3CF', desc: 'Medium Performance\nMedium Potential' },
                      { name: "Promotable", color: '#008000', bgColor: '#DCF3D9', desc: 'High Performance\nMedium Potential' },
                      { name: "Poor Fit", color: '#C51E1E', bgColor: '#F9ECEC', desc: 'Low Performance\nLow Potential' },
                      { name: "Suitably Placed", color: '#D68910', bgColor: '#FCF3CF', desc: 'Medium Performance\nLow Potential' },
                      { name: "Valued Professional", color: '#D68910', bgColor: '#FCF3CF', desc: 'High Performance\nLow Potential' }
                    ];

                    return categories.map((category, index) => {
                      const isCurrentEmployee = currentBoxData === category.name;
                      const opacity = isCurrentEmployee ? 1 : 0.3;
                      const boxShadow = isCurrentEmployee ? '0 1px 4px rgba(0,0,0,0.15)' : 'none';
                      const transform = isCurrentEmployee ? 'scale(1.01)' : 'scale(1)';
                      const borderWidth = isCurrentEmployee ? '2px' : '1px';

                      return (
                        <div 
                          key={category.name}
                          style={{ 
                            padding: '4px', 
                            backgroundColor: category.bgColor, 
                            border: `${borderWidth} solid ${category.color}`, 
                            borderRadius: '4px',
                            textAlign: 'center',
                            opacity: opacity,
                            boxShadow: boxShadow,
                            transform: transform,
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div style={{ 
                            fontWeight: 'bold', 
                            color: category.color, 
                            marginBottom: '2px',
                            fontSize: isCurrentEmployee ? '8px' : '7px',
                            lineHeight: '1'
                          }}>
                            {category.name}
                          </div>
                          <div style={{ fontSize: '6px', color: '#666', lineHeight: '1' }}>
                            {category.desc.split('\n').map((line, i) => (
                              <div key={i}>{line}</div>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </Card>
            </Space>
          </Col>
        </Row>

        {/* Promotion Readiness - Full Width Section */}
        <Card 
          title={
            <Space>
              <RocketOutlined style={{ color: '#52c41a' }} />
              <Tooltip 
                title="Status and comments from 2024 feedback on Culture Amp"
                placement="top"
                mouseEnterDelay={0}
                mouseLeaveDelay={0}
                overlayClassName="promotion-tooltip"
              >
                <span 
                  style={{ 
                    fontWeight: 'bold', 
                    color: '#333'
                  }}
                >
                  Promotion Readiness
                </span>
              </Tooltip>
            </Space>
          }
          style={{ 
            marginBottom: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
          }}
          headStyle={{ 
            background: 'linear-gradient(90deg, #f6ffed 0%, #f0f9e7 100%)',
            borderRadius: '12px 12px 0 0'
          }}
        >
          <PromotionMetrices 
            key={`promotion-${data?.userPrincipalName}-${readinessValue}-${isReadyValue}`}
            data={data} 
            value={textValue} 
            onChange={handleTextareaChange} 
            readiness={readinessValue} 
            onReadinessChange={handleReadinessChange} 
            isReady={isReadyValue} 
            isReadyChange={handleIsReadyChange} 
          />
        </Card>

        {/* Succession Planning - Full Width Section */}
        <Card 
          title={
            <Space>
              <TeamOutlined style={{ color: '#722ed1' }} />
              <span>Succession Planning</span>
            </Space>
          }
          style={{ 
            marginBottom: '24px',
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)'
          }}
          headStyle={{ 
            background: 'linear-gradient(90deg, #f9f0ff 0%, #efdbff 100%)',
            borderRadius: '12px 12px 0 0'
          }}
        >
          <SuccessorComponent 
            user={data} 
            successorData={successorData} 
            setsuccessorData={setsuccessorData} 
          />
        </Card>

        {/* Action Buttons */}
        <Card 
          style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
            marginBottom: '24px'
          }}
          bodyStyle={{
            padding: '24px',
            textAlign: 'center'
          }}
        >
          <Space size="large">
            <Button 
              type="primary" 
              size="large"
              icon={<SaveOutlined />}
              onClick={handlesave}
              loading={isSaving}
              style={{
                height: '56px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                border: 'none',
                boxShadow: '0 6px 20px rgba(82, 196, 26, 0.3)',
                minWidth: '200px'
              }}
            >
              {isSaving ? 'Saving Assessment...' : 'Save Assessment'}
            </Button>
            <Button 
              size="large"
              icon={<CloseOutlined />}
              onClick={onSaveComplete}
              style={{
                height: '56px',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '600',
                minWidth: '150px'
              }}
            >
              Close
            </Button>
            
          </Space>
        </Card>
      </div>
    </div>
  );
};

export default AssessmentPage;